import classNames from 'classnames';
import React, { ButtonHTMLAttributes, KeyboardEvent, MouseEvent } from 'react';
import { semantic_colors } from '../../foundation';
import { IconX } from '../icons/generated';
import { InputSize } from '../input/types';
import {
  INPUT_CHIP_DELETE_ICON_SIZE,
  getInputChipSizeStyle,
  input_chip_base_css,
  input_chip_x_disabled_css,
  input_chip_x_enabled_css,
} from './styles';

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** @default 'medium' */
  size?: InputSize;
  /** @default true */
  canDelete?: boolean;
  onDelete?: () => void;
}

const isDeleteButtonEventTarget = (target: EventTarget) => {
  return target instanceof Element && Boolean(target.closest('.pds-input-chip-delete-button'));
};

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  (
    { children, onDelete, size = 'medium', canDelete = true, disabled, className, onClick, onKeyDown, ...props },
    ref,
  ) => {
    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (canDelete && isDeleteButtonEventTarget(e.target)) {
        e.stopPropagation();
        if (!disabled) {
          onDelete?.();
        }
        return;
      }

      onClick?.(e);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      onKeyDown?.(e);

      if (e.defaultPrevented || !canDelete || !onDelete || disabled || (e.key !== 'Delete' && e.key !== 'Backspace')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      onDelete();
    };

    return (
      <button
        type='button'
        className={classNames('pds-input-chip', className)}
        css={[input_chip_base_css, getInputChipSizeStyle(size, canDelete)]}
        disabled={disabled}
        {...props}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        ref={ref}
      >
        {children}
        {canDelete && (
          <span
            aria-hidden='true'
            className='pds-input-chip-delete-button'
            css={[disabled ? input_chip_x_disabled_css : input_chip_x_enabled_css]}
          >
            <IconX
              size={INPUT_CHIP_DELETE_ICON_SIZE}
              color={disabled ? semantic_colors.content.disabled : semantic_colors.content.primary}
            />
          </span>
        )}
      </button>
    );
  },
);
